import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { FaceScanService } from "@api/face-scan/src";
import { TranslateService } from "@ngx-translate/core";
import { CacheService } from "@shared/services/cache.service";
import { ToastService } from "@shared/services/toast/toast.service";
import { map } from "rxjs";

const getFaceScanSummary = () => {
	const cacheService = inject(CacheService);
	const faceScanService = inject(FaceScanService);

	return cacheService.getOrFetch("faceScanSummary", () =>
		faceScanService.faceScanGetSummaryEndpoint().pipe(map((res) => res.data)),
	);
};

// hasAccess comes from the current company/campaign, but from the user's point of view
// "no access" always means "you need an active subscription" - the guard just silently
// bounced them to /home before, with no explanation at all.
// toastService/translate must be resolved here, synchronously, while the guard's
// injection context is active - inject() cannot be called later inside the map() callback,
// which runs asynchronously once the observable emits.
const denyWithSubscriptionMessage = (
	router: Router,
	toastService: ToastService,
	translate: TranslateService,
): boolean => {
	toastService.warning(translate.instant("face-scan.subscription-required"));
	router.navigate(["/home"]);
	return false;
};

export const hasAnyFaceScanAccess: CanActivateFn = () => {
	const router = inject(Router);
	const toastService = inject(ToastService);
	const translate = inject(TranslateService);

	return getFaceScanSummary().pipe(
		map((summary) => {
			if (!summary?.hasAccess && !summary?.hasHistoricAccess) {
				return denyWithSubscriptionMessage(router, toastService, translate);
			}
			return true;
		}),
	);
};

export const hasFaceScanAccess: CanActivateFn = () => {
	const router = inject(Router);
	const toastService = inject(ToastService);
	const translate = inject(TranslateService);

	return getFaceScanSummary().pipe(
		map((summary) => {
			if (!summary?.hasAccess) {
				return denyWithSubscriptionMessage(router, toastService, translate);
			}
			return true;
		}),
	);
};

export const hasDoneFaceScan: CanActivateFn = () => {
	const router = inject(Router);

	return getFaceScanSummary().pipe(
		map((summary) => {
			if (!summary?.hasDoneFaceScan) {
				router.navigate(["/face-scan/introduction"]);
				return false;
			}
			return true;
		}),
	);
};
